import { decodeAbiParameters, isAddressEqual, parseAbiParameters, type Address } from "viem";

import { LOAN_ADDRESSES } from "../../contracts";
import { isAmortizedModel } from "../../contracts/interestRateModels";

/**
 * Off-chain port of the V2 repayment schedule.
 *
 * Mirrors the on-chain math exactly:
 *  - calendar deadlines       -> ScheduleLogic.deadlines
 *  - per-window principal/int -> {Simple,Amortized}InterestRateModel.repayment
 *  - state transition         -> LoanRouterV2.repay (balance -= principal; repaymentCount++)
 *
 * All internal math runs in SCALED 18-decimal space, identical to the IRM. Use
 * `unscale(x, decimals)` to render in the loan currency's decimals (matches the
 * router's `quote`, which rounds up).
 *
 * Assumes on-time payments and no prepayment. Late grace-period interest
 * (`timestamp > deadline`) is intentionally omitted — for "what's owed now if
 * late" use the on-chain `quote(terms, ts)`.
 */

const FIXED_POINT_SCALE = 10n ** 18n;
const SECONDS_PER_DAY = 86_400;

export interface ScheduleTerms {
  chain: number;
  repaymentDay: number;
  totalDurationDays: number;
  timezoneOffsetSeconds: number;
  interestRateModel: Address;
  principalAndInterestStubPayment: boolean;
  trancheSpecs: readonly { amount: bigint; rate: bigint }[];
  feeSpecs: readonly { kind: number; model: Address; options: `0x${string}` }[];
}

export interface Repayment {
  deadline: number;
  date: Date;
  principal: bigint;
  interest: bigint;
  fee: bigint;
  total: bigint;
  remainingBalance: bigint;
}

export interface PaymentScheduleResult {
  repayments: Repayment[];
  totalPrincipal: bigint;
  totalInterest: bigint;
  totalFees: bigint;
}

export interface ScheduleState {
  originationTimestamp: number;
  repaymentCount?: number;
  scaledBalance?: bigint;
  currencyDecimals?: number;
}

// The `a === 0n` guard is load-bearing: BigInt division truncates toward zero,
// so without it `(0n - 1n) / b + 1n` evaluates to `0n + 1n = 1n`, wrongly
// reporting 1 wei of interest for a zero-rate window. Matches Math.Rounding.Ceil.
const ceilDiv = (a: bigint, b: bigint) => (a === 0n ? 0n : (a - 1n) / b + 1n);
const mulDiv = (a: bigint, b: bigint, d: bigint) => (a * b) / d; // floor

/** Mirror of LoanRouterV2._unscale: round up when there is a remainder. */
export function unscale(scaled: bigint, decimals: number, roundUp = true): bigint {
  const factor = 10n ** BigInt(18 - decimals);
  if (factor === 1n) return scaled;
  return scaled % factor === 0n || !roundUp ? scaled / factor : scaled / factor + 1n;
}

/*--------------------------------------------------------------------------*/
/* Calendar schedule — port of ScheduleLogic.deadlines (UTC)                */
/*--------------------------------------------------------------------------*/

// solady DateTimeLib equivalents on the UTC civil calendar
const timestampToDate = (ts: number) => {
  const d = new Date(ts * 1000);
  return { year: d.getUTCFullYear(), month: d.getUTCMonth() + 1, day: d.getUTCDate() };
};
const dateToTimestamp = (year: number, month: number, day: number) => Math.floor(Date.UTC(year, month - 1, day) / 1000);
const daysInMonth = (year: number, month: number) => new Date(Date.UTC(year, month, 0)).getUTCDate();
const addMonth = (year: number, month: number): [number, number] => (month === 12 ? [year + 1, 1] : [year, month + 1]);

function anchorDay(loan: ScheduleTerms, year: number, month: number) {
  const dim = daysInMonth(year, month);
  return loan.repaymentDay > dim ? dim : loan.repaymentDay;
}

function anchorUtc(loan: ScheduleTerms, year: number, month: number) {
  const local = dateToTimestamp(year, month, anchorDay(loan, year, month));
  return local - loan.timezoneOffsetSeconds;
}

/** Returns the stub flag and the UTC deadline schedule for a given origination. */
export function computeDeadlines(
  loan: ScheduleTerms,
  originationTimestamp: number,
): { hasStub: boolean; deadlines: number[] } {
  const local = originationTimestamp + loan.timezoneOffsetSeconds;
  const { year: oy, month: om, day } = timestampToDate(local);
  const anchorThisMonth = anchorDay(loan, oy, om);
  const hasStub = day !== anchorThisMonth;
  let [year, month] = day >= anchorThisMonth ? addMonth(oy, om) : [oy, om];

  const durationFloor = originationTimestamp + loan.totalDurationDays * SECONDS_PER_DAY;

  const deadlines: number[] = [];
  while (anchorUtc(loan, year, month) < durationFloor) {
    deadlines.push(anchorUtc(loan, year, month));
    [year, month] = addMonth(year, month);
  }
  // closing anchor: first anchor at or after the floor
  deadlines.push(anchorUtc(loan, year, month));

  return { hasStub, deadlines };
}

/*--------------------------------------------------------------------------*/
/* Interest rate model math (scaled 18-dec)                                 */
/*--------------------------------------------------------------------------*/

function getBlendedRate(loan: ScheduleTerms): bigint {
  let weighted = 0n;
  let principal = 0n;
  for (const t of loan.trancheSpecs) {
    weighted += t.rate * t.amount;
    principal += t.amount;
  }
  return principal === 0n ? 0n : weighted / principal;
}

/** Sum of unscaled tranche amounts (loan currency decimals). */
function getPrincipal(loan: ScheduleTerms): bigint {
  return loan.trancheSpecs.reduce((sum, t) => sum + t.amount, 0n);
}

const windowDuration = (deadlines: number[], i: number, origination: number) =>
  BigInt(deadlines[i] - (i === 0 ? origination : deadlines[i - 1]));

/** Port of AmortizedInterestRateModel._computeLevelPayment */
function computeLevelPayment(
  scaledBalance: bigint,
  repaymentCount: number,
  blendedRate: bigint,
  deadlines: number[],
  origination: number,
): bigint {
  let discountSum = 0n;
  let runningProduct = FIXED_POINT_SCALE;
  for (let r = deadlines.length; r > repaymentCount; r--) {
    const index = r - 1;
    const duration = windowDuration(deadlines, index, origination);
    discountSum += runningProduct;
    runningProduct = mulDiv(runningProduct, FIXED_POINT_SCALE + blendedRate * duration, FIXED_POINT_SCALE);
  }
  return mulDiv(scaledBalance, runningProduct, discountSum);
}

/*--------------------------------------------------------------------------*/
/* Fees — port of LoanLogicV2._computeFees                                  */
/*--------------------------------------------------------------------------*/

const REPAYMENT_FEE_KIND = 1;
const EXIT_FEE_KIND = 2;
const RATIO_MODE_AMOUNT = 1;

function computeFeesForKind(
  loan: ScheduleTerms,
  kind: number,
  balanceScaled: bigint,
  principalScaled: bigint,
  currencyDecimals: number,
): bigint {
  const { ratioFeeModel, absoluteFeeModel } = LOAN_ADDRESSES[loan.chain];
  let total = 0n;
  for (const spec of loan.feeSpecs) {
    if (spec.kind !== kind) continue;
    if (isAddressEqual(spec.model, ratioFeeModel)) {
      const [mode, rate] = decodeAbiParameters(parseAbiParameters("uint8, uint256"), spec.options);
      if (rate === 0n) continue;
      const base = mode === RATIO_MODE_AMOUNT ? principalScaled : balanceScaled;
      total += ceilDiv(base * rate, FIXED_POINT_SCALE);
    } else if (isAddressEqual(spec.model, absoluteFeeModel)) {
      const [amount] = decodeAbiParameters(parseAbiParameters("uint256"), spec.options);
      total += amount * 10n ** BigInt(18 - currencyDecimals);
    }
  }
  return total;
}

/*--------------------------------------------------------------------------*/
/* Full forward schedule                                                    */
/*--------------------------------------------------------------------------*/

/**
 * Classified against every model generation ever deployed, not just the current
 * one — loan terms embed whichever address was current at origination, so live
 * loans span generations. See `contracts/interestRateModels.ts`.
 */
export function isAmortized(loan: ScheduleTerms): boolean {
  return isAmortizedModel(loan.chain, loan.interestRateModel);
}

export function calcPaymentSchedule(loan: ScheduleTerms, state: ScheduleState): PaymentScheduleResult {
  if (loan.trancheSpecs.length === 0)
    return { repayments: [], totalPrincipal: 0n, totalInterest: 0n, totalFees: 0n };

  const { originationTimestamp } = state;
  const repaymentCount = state.repaymentCount ?? 0;
  const currencyDecimals = state.currencyDecimals ?? 18;
  if (currencyDecimals > 18) throw new Error(`unsupported currency decimals ${currencyDecimals} (must be <= 18)`);
  const scaledBalance = state.scaledBalance ?? getPrincipal(loan) * 10n ** BigInt(18 - currencyDecimals);
  const principalScaled = getPrincipal(loan) * 10n ** BigInt(18 - currencyDecimals);

  const blendedRate = getBlendedRate(loan);
  const { hasStub, deadlines } = computeDeadlines(loan, originationTimestamp);
  const amortized = isAmortized(loan);
  const piStub = loan.principalAndInterestStubPayment;

  let balance = scaledBalance;
  let totalPrincipal = 0n;
  let totalInterest = 0n;
  let totalFees = 0n;
  const repayments: Repayment[] = [];

  for (let i = repaymentCount; i < deadlines.length; i++) {
    const duration = windowDuration(deadlines, i, originationTimestamp);
    const interest = ceilDiv(balance * blendedRate * duration, FIXED_POINT_SCALE);

    const remaining = deadlines.length - i;
    let principal: bigint;
    if (i === 0 && hasStub && !piStub && deadlines.length > 1) {
      // Stub first window is interest only
      principal = 0n;
    } else if (amortized) {
      if (remaining === 1) {
        principal = balance; // last window sweeps the balance
      } else {
        const level = computeLevelPayment(balance, i, blendedRate, deadlines, originationTimestamp);
        const p = level > interest ? level - interest : 0n;
        principal = p < balance ? p : balance; // Math.min(p, balance)
      }
    } else {
      // Simple: equal installment of the remaining balance; last sweeps remainder
      principal = balance / BigInt(remaining);
    }

    let fee = computeFeesForKind(loan, REPAYMENT_FEE_KIND, balance, principalScaled, currencyDecimals);
    if (balance - principal === 0n) {
      fee += computeFeesForKind(loan, EXIT_FEE_KIND, balance, principalScaled, currencyDecimals);
    }

    balance -= principal;
    totalPrincipal += principal;
    totalInterest += interest;
    totalFees += fee;
    repayments.push({
      deadline: deadlines[i],
      date: new Date(deadlines[i] * 1000),
      principal,
      interest,
      fee,
      total: principal + interest + fee,
      remainingBalance: balance,
    });
  }

  return { repayments, totalPrincipal, totalInterest, totalFees };
}

/** Total (scaled 18-dec) due for the next upcoming window. */
export function calcNextPaymentAmount(loan: ScheduleTerms, state: ScheduleState): bigint {
  return calcPaymentSchedule(loan, state).repayments[0]?.total ?? 0n;
}
